import type { AxiosError } from 'axios';
import { Button, Center, Checkbox, Group, Loader, Stack, Text } from '@mantine/core';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { WeekReport } from '@/api/generated/models';
import {
  getGetInvoiceQueryKey,
  getListInvoicesQueryKey,
  getListInvoiceWeekReportsQueryKey,
  listInvoiceWeekReports,
  useAddWeekReport,
  useListInvoices
} from '@/api/generated/invoices/invoices';
import { WeekReportStatus } from '@/api/generated/models/weekReportStatus';
import { useListWeekReports } from '@/api/generated/week-reports/week-reports';
import { FormModal, ModalFooter, MutationButton, TextField, Toast } from '@/components/common';
import { formatWeekLabel, formatWeekPeriod } from '@/features/week-reports/weekReportDisplay';
import { formatAmount } from '@/utils/format';

interface AddWeekReportToInvoiceModalProps {
  opened: boolean;
  onClose: () => void;
  invoiceId: string;
  contractId: string;
  excludeWeekReportIds: string[];
}

export const AddWeekReportToInvoiceModal = ({
  opened,
  onClose,
  invoiceId,
  contractId,
  excludeWeekReportIds
}: AddWeekReportToInvoiceModalProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [isAdding, setIsAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const { data: reportsData, isPending: isReportsPending } = useListWeekReports({
    query: { enabled: opened && !!contractId }
  });
  const { data: invoicesData, isPending: isInvoicesPending } = useListInvoices({
    query: { enabled: opened && !!contractId }
  });
  const contractInvoiceIds = (invoicesData ?? [])
    .filter((invoice) => invoice.contractId === contractId && invoice.id)
    .map((invoice) => invoice.id as string);
  const invoiceLinkQueries = useQueries({
    queries: contractInvoiceIds.map((linkedInvoiceId) => ({
      queryKey: getListInvoiceWeekReportsQueryKey(linkedInvoiceId),
      queryFn: () => listInvoiceWeekReports(linkedInvoiceId),
      enabled: opened
    }))
  });
  const excludeSet = new Set(excludeWeekReportIds);
  invoiceLinkQueries.forEach((linkQuery) =>
    (linkQuery.data ?? []).forEach((link) => link.weekReportId && excludeSet.add(link.weekReportId))
  );
  const isLoading =
    isReportsPending ||
    isInvoicesPending ||
    invoiceLinkQueries.some((linkQuery) => linkQuery.isPending);
  const eligible: WeekReport[] = (isLoading ? [] : (reportsData ?? [])).filter(
    (report) =>
      report.contractId === contractId &&
      report.status === WeekReportStatus.APPROVED &&
      !!report.id &&
      !excludeSet.has(report.id)
  );

  const query = search.trim().toLowerCase();
  const filteredRows = eligible.filter((report) => {
    if (!query) return true;
    return formatWeekLabel(report.weekNumber, report.reportYear).toLowerCase().includes(query);
  });

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const reset = () => {
    setSearch('');
    setSelected(new Set());
    setAddError(null);
  };

  const addWeekReportMutation = useAddWeekReport({
    mutation: { meta: { skipGlobalErrorToast: true } }
  });

  // Namjerno sekvencijalno (isti obrazac kao AddActivityToWeekReportModal) — svaki
  // dodani izvještaj odmah ponovno preračunava ukupne iznose fakture na backendu.
  const handleAdd = async () => {
    setIsAdding(true);
    setAddError(null);
    try {
      for (const weekReportId of selected) {
        await addWeekReportMutation.mutateAsync({ id: invoiceId, data: { weekReportId } });
        setSelected((prev) => {
          const next = new Set(prev);
          next.delete(weekReportId);
          return next;
        });
      }
      reset();
      onClose();
    } catch (error) {
      const backendMessage = (error as AxiosError<{ error?: string }>)?.response?.data?.error;
      setAddError(typeof backendMessage === 'string' ? backendMessage : t('common.error'));
    } finally {
      queryClient.invalidateQueries({ queryKey: getListInvoiceWeekReportsQueryKey(invoiceId) });
      queryClient.invalidateQueries({ queryKey: getGetInvoiceQueryKey(invoiceId) });
      queryClient.invalidateQueries({ queryKey: getListInvoicesQueryKey() });
      setIsAdding(false);
    }
  };

  return (
    <FormModal
      opened={opened}
      onClose={() => {
        reset();
        onClose();
      }}
      size={640}
      title={
        <Text fz={20} fw={700} style={{ color: 'var(--color-text-primary)' }}>
          {t('invoiceDetail.addWeekReportModal.title')}
        </Text>
      }
      styles={{
        content: { backgroundColor: 'var(--color-bg-surface)' },
        header: {
          backgroundColor: 'var(--color-bg-surface)',
          borderBottom: '1px solid var(--color-border-soft)'
        },
        body: { padding: 20 }
      }}
    >
      <Stack gap={16}>
        <TextField
          hideLabel
          placeholder={t('invoiceDetail.addWeekReportModal.searchPlaceholder')}
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
        />

        <Stack gap={0} style={{ maxHeight: 320, overflowY: 'auto' }}>
          {filteredRows.length > 0 && (
            <Group
              gap={8}
              wrap="nowrap"
              py={4}
              style={{ borderBottom: '1px solid var(--color-border-soft)' }}
            >
              <Checkbox
                readOnly
                checked={false}
                tabIndex={-1}
                style={{ visibility: 'hidden' }}
                aria-hidden
              />
              <Text
                fz={11}
                fw={600}
                w={200}
                style={{ color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}
              >
                {t('invoiceDetail.colPeriod')}
              </Text>
              <Text
                fz={11}
                fw={600}
                ta="right"
                style={{ flex: 1, color: 'var(--color-text-muted)' }}
              >
                {t('invoiceDetail.colAmount')}
              </Text>
            </Group>
          )}

          {isLoading && (
            <Center py={24}>
              <Loader size="sm" />
            </Center>
          )}

          {!isLoading && filteredRows.length === 0 && (
            <Text fz={13} py={24} ta="center" style={{ color: 'var(--color-text-muted)' }}>
              {t('invoiceDetail.addWeekReportModal.emptyState')}
            </Text>
          )}

          {filteredRows.map((report, index) => (
            <Group
              key={report.id}
              gap={8}
              wrap="nowrap"
              py={10}
              role="button"
              tabIndex={0}
              onClick={() => report.id && toggle(report.id)}
              onKeyDown={(event) => {
                if ((event.key === 'Enter' || event.key === ' ') && report.id) toggle(report.id);
              }}
              style={{
                cursor: 'pointer',
                borderTop: index === 0 ? undefined : '1px solid var(--color-border-soft)'
              }}
            >
              <Checkbox
                checked={!!report.id && selected.has(report.id)}
                onChange={() => report.id && toggle(report.id)}
                onClick={(event) => event.stopPropagation()}
              />
              <Text
                fz={13}
                fw={600}
                w={200}
                style={{ color: 'var(--color-text-primary)', whiteSpace: 'nowrap' }}
              >
                {formatWeekPeriod(report.weekNumber, report.reportYear)}
              </Text>
              <Text fz={13} ta="right" style={{ flex: 1, color: 'var(--color-text-secondary)' }}>
                {formatAmount(report.totalAmount)}
              </Text>
            </Group>
          ))}
        </Stack>

        {addError && <Toast tone="danger">{addError}</Toast>}

        <ModalFooter>
          <Button
            variant="outline"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            {t('common.cancel')}
          </Button>
          <MutationButton isPending={isAdding} disabled={selected.size === 0} onClick={handleAdd}>
            {t('invoiceDetail.addWeekReportModal.addSelected', { count: selected.size })}
          </MutationButton>
        </ModalFooter>
      </Stack>
    </FormModal>
  );
};
